// Chat list (Nachrichten) pieces in the mobile Pro chat list's look
// (Kjuup-Mobile-App src/components/home/v2/messages/pro/MessagesProParts.js and
// ChatRowPro.js): the 36 avatar (Teams size) with the unread counter, the live typing line,
// the row menu, the filter chips, the empty card, the skeleton and the
// join-request card. Presentation only: every action calls a handler of
// LatestMessages. Styles: LatestMessagesPro.css (lmp-*).
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import MaterialIcon from "../../components/MaterialIcon";
import EmailSenderAvatar from "../../components/EmailSenderAvatar";
import { AVATAR_INK, firstNameOf, initialsOf, pastelFor, toneOf } from "./chatListModel";
import { useChatLive } from "./useChatListLive";
import teamsLogo from "../../Icons/Teams.png";

const countText = (n) => (n > 99 ? "99+" : String(n));

/** The online dot of a person's avatar (live userStatus events). */
function LiveOnlineDot({ partnerId }) {
  const { online } = useChatLive(null, partnerId);
  return online ? <span className="lmp-avatar__online" aria-hidden="true" /> : null;
}

/**
 * Chat avatar, 36 (Teams size):
 *   direct  circle, pastel with initials or the photo, online dot
 *   room    rounded square in the room's colour (tint + initials) or the room photo
 *   group   rounded square with the group glyph in the group's colour
 *   self    My Space: soft accent circle with chat_paste_go
 *   mail    rounded square with the sender's logo / initials
 * The unread counter sits on the top-right corner; `selected` draws the
 * multi-select check over it. `teams` marks a Microsoft Teams mirror with the
 * Teams logo on the bottom-right corner (Teams chats have no online dot).
 */
export function ChatListAvatar({ kind = "direct", name = "", photoUrl, color, unread = 0, partnerId, colleague = false, selected = false, email, teams = false }) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setFailed(false);
    setLoaded(false);
  }, [photoUrl]);

  const tone = kind === "room" || kind === "group" ? toneOf(color) : null;
  const showPhoto = (kind === "direct" || kind === "room") && !!photoUrl && !failed;
  const vars = {};
  if (kind === "direct") {
    vars["--lmp-av-bg"] = pastelFor(name);
    vars["--lmp-av-fg"] = AVATAR_INK;
  } else if (tone) {
    vars["--lmp-av-bg"] = tone.bg;
    vars["--lmp-av-fg"] = tone.fg;
  }

  let inner = null;
  if (kind === "self") inner = <MaterialIcon icon="chat_paste_go" size={20} />;
  else if (kind === "group") inner = <MaterialIcon icon="group" size={20} />;
  else if (kind === "mail") inner = <EmailSenderAvatar email={email} name={name} size={36} className="lmp-avatar__mail" />;
  else inner = <span className="lmp-avatar__initials">{initialsOf(name) || "·"}</span>;

  const n = Number(unread) || 0;
  return (
    <div className={`lmp-avatar lmp-avatar--${kind}${colleague ? " lmp-avatar--colleague" : ""}${selected ? " is-selected" : ""}`} aria-hidden="true">
      <div className="lmp-avatar__tile" style={vars}>
        {inner}
        {showPhoto ? (
          <img
            src={photoUrl}
            alt=""
            decoding="async"
            className={`lmp-avatar__photo${loaded ? " is-loaded" : ""}`}
            ref={(node) => {
              if (node && node.complete && node.naturalWidth > 0 && !loaded) setLoaded(true);
            }}
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
          />
        ) : null}
        {selected ? (
          <span className="lmp-avatar__check">
            <MaterialIcon icon="check" size={20} weight={600} />
          </span>
        ) : null}
      </div>
      {kind === "direct" && partnerId && !selected ? <LiveOnlineDot partnerId={partnerId} /> : null}
      {teams && !selected ? (
        <span className="lmp-avatar__teams">
          <img src={teamsLogo} alt="" decoding="async" />
        </span>
      ) : null}
      {n > 0 && !selected ? <span className="lmp-avatar__count">{countText(n)}</span> : null}
    </div>
  );
}

/**
 * The preview line, or "typing…" while someone types in the chat (live):
 * "schreibt…" for a person, "Lisa schreibt…" in rooms and groups.
 */
export function LivePreview({ groupId, kind, typingLabel, isTypingLabel, children }) {
  const { typing } = useChatLive(groupId, null);
  if (!typing) return children;
  const first = kind === "direct" || kind === "self" ? "" : firstNameOf(typing.name);
  const text = first ? `${first} ${isTypingLabel}…` : typingLabel;
  return <span className="lmp-row__typing">{text}</span>;
}

/** "MAIL" / "BERICHT" outline tag before a mail's subject. */
export function KindTag({ label }) {
  if (!label) return null;
  return <span className="lmp-kind-tag">{String(label).toUpperCase()}</span>;
}

/**
 * Row menu (the mobile row's ⋮ sheet), opened from the ⋮ button or a right
 * click. menu = { x, y, align: "start" | "end", anchorTop, title, items }
 * items [{ key, icon, label, onSelect, active, danger }].
 */
export function ChatRowMenu({ menu, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = menu.align === "end" ? menu.x - w : menu.x;
    let y = menu.y;
    if (y + h > vh - 8) y = Math.max(8, (menu.anchorTop != null ? menu.anchorTop : menu.y) - h);
    x = Math.max(8, Math.min(x, vw - w - 8));
    setPos({ x, y });
  }, [menu]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    const onScroll = (e) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    };
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onClose);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [onClose]);

  useEffect(() => {
    if (!pos || !ref.current) return;
    const first = ref.current.querySelector(".lmp-menu__item");
    if (first) first.focus();
  }, [pos]);

  const style = pos ? { "--lmp-menu-x": `${pos.x}px`, "--lmp-menu-y": `${pos.y}px` } : undefined;
  return ReactDOM.createPortal(
    <div
      ref={ref}
      className={`lmp-menu${pos ? " is-ready" : ""}`}
      style={style}
      role="menu"
      aria-label={menu.title || undefined}
      onContextMenu={(e) => e.preventDefault()}
    >
      {menu.title ? <div className="lmp-menu__title">{menu.title}</div> : null}
      {(menu.items || []).map((item) => (
        <button
          key={item.key}
          type="button"
          role="menuitem"
          className={`kj-btn lmp-menu__item${item.danger ? " is-danger" : ""}${item.active ? " is-active" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
            if (item.onSelect) item.onSelect();
          }}
        >
          <MaterialIcon icon={item.icon} size={20} fill={item.active ? 1 : 0} />
          <span>{item.label}</span>
        </button>
      ))}
    </div>,
    document.body
  );
}

/** Hover / focus quick actions of a row (the mobile row's swipe tiles). */
export function RowQuickActions({ actions }) {
  if (!actions || !actions.length) return null;
  return (
    <div className="lmp-row__quick">
      {actions.map((a) => (
        <button
          key={a.key}
          type="button"
          className={`kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral lmp-row__quick-btn${a.active ? " is-active" : ""}${a.danger ? " kj-btn--danger" : ""}`}
          title={a.label}
          aria-label={a.label}
          aria-pressed={a.active != null ? !!a.active : undefined}
          disabled={!!a.disabled}
          onClick={(e) => {
            e.stopPropagation();
            if (a.onSelect) a.onSelect();
          }}
        >
          <MaterialIcon icon={a.icon} size={20} fill={a.active ? 1 : 0} />
        </button>
      ))}
    </div>
  );
}

/**
 * A mail row (mobile EmailRowPro, look "chat"): the sender's logo with the unread counter, the
 * sender, an optional room chip and the time, then the MAIL / report tag, the clip and the
 * subject, with the hover quick actions. The Messages list and the room's mail lists
 * (components/EmailFeedRow) both draw it, so a mail looks the same in both places.
 */
export function MailChatRow({ name, email, subject, time, tag, unread = false, open = false, checked = false, attachment = false, attachmentLabel, roomChip = null, quick, ariaLabel, ariaPressed, onOpen, onContextMenu }) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      className={`lmp-row lmp-row--mail${open ? " is-open" : ""}${checked ? " is-selected" : ""}${unread ? " is-unread" : ""}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (onOpen) onOpen();
        }
      }}
      onContextMenu={onContextMenu}
    >
      <ChatListAvatar kind="mail" name={name} email={email} unread={unread ? 1 : 0} selected={!!checked} />
      <div className="lmp-row__text">
        <div className="lmp-row__line1">
          <span className="lmp-row__name">{name || "Unknown"}</span>
          {roomChip}
          <span className="lmp-row__time">{time}</span>
        </div>
        <div className="lmp-row__line2">
          <span className="lmp-row__preview">
            <KindTag label={tag} />
            {attachment ? <MaterialIcon icon="attach_file" size={16} className="lmp-row__glyph" title={attachmentLabel} /> : null}
            <span className="lmp-row__preview-text">{subject}</span>
          </span>
          <RowQuickActions actions={quick} />
        </div>
      </div>
    </div>
  );
}

function ChipRow({ chips, activeKey, onSelect, sub = false, label }) {
  const row = chips.map((chip) => {
    const active = chip.key === activeKey;
    const n = Number(chip.count) || 0;
    return (
      <button
        key={chip.key}
        type="button"
        role="tab"
        aria-selected={active}
        className={sub ? `kj-segment${active ? " is-active" : ""}` : `kj-tab lmp-chip${active ? " is-active" : ""}`}
        onClick={() => onSelect(chip.key)}
      >
        <span>{chip.label}</span>
        {n > 0 ? <span className="lmp-chip__count">{countText(n)}</span> : null}
      </button>
    );
  });
  // The second row is a segmented control, as on a project's Dateien tab.
  if (sub) {
    return (
      <div className="lmp-chips__row lmp-chips__row--sub">
        <div className="kj-segmented" role="tablist" aria-label={label}>
          {row}
        </div>
      </div>
    );
  }
  return (
    <div className="lmp-chips__row" role="tablist" aria-label={label}>
      {row}
    </div>
  );
}

/**
 * The filter chips: All · Unread n · Rooms · Groups · Direct · E-Mail (one active),
 * and under Rooms / Groups the compact All / Internal / External (/ Oversight) row.
 * `extra` (the company choice) goes under the rows.
 */
export function ChatListChips({ chips, activeKey, onSelect, subChips, subActive, onSubSelect, extra, label, subLabel }) {
  return (
    <div className="lmp-chips">
      <ChipRow chips={chips} activeKey={activeKey} onSelect={onSelect} label={label} />
      {subChips && subChips.length ? (
        <ChipRow chips={subChips} activeKey={subActive} onSelect={onSubSelect} sub label={subLabel} />
      ) : null}
      {extra ? <div className="lmp-chips__extra">{extra}</div> : null}
    </div>
  );
}

/** Empty filter: one centred line in a rounded card. */
export function ChatListEmpty({ label }) {
  return (
    <div className="lmp-empty" role="status">
      {label}
    </div>
  );
}

/** Loading rows: a 36 tile and two bars per row (bar widths vary per row in the CSS). */
export function ChatListSkeleton({ count = 6, compact = false }) {
  return (
    <div className={`lmp-skeleton${compact ? " lmp-skeleton--compact" : ""}`} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="lmp-skeleton__row">
          <div className={`lmp-skeleton__tile${i % 3 === 2 ? " is-round" : ""}`} />
          <div className="lmp-skeleton__lines">
            <div className="lmp-skeleton__bar" />
            <div className="lmp-skeleton__bar lmp-skeleton__bar--short" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** "Pending room invitations" card: tile with the count, who invited you, chevron. */
export function MessagesJoinCard({ count, title, subtitle, onClick }) {
  if (!(Number(count) > 0)) return null;
  return (
    <button type="button" className="kj-btn lmp-join" onClick={onClick}>
      <span className="lmp-join__tile">
        <MaterialIcon icon="group_add" size={22} />
        <span className="lmp-join__count">{countText(Number(count))}</span>
      </span>
      <span className="lmp-join__text">
        <span className="lmp-join__title">{title}</span>
        {subtitle ? <span className="lmp-join__subtitle">{subtitle}</span> : null}
      </span>
      <MaterialIcon icon="chevron_right" size={22} className="lmp-join__chevron" />
    </button>
  );
}
